import { getGoogleDomainHint } from "./operators";

const LOGIN_HINT_KEY = "operators:google-login-hint";
const AUTO_SIGN_IN_SUPPRESSED_KEY = "operators:google-auto-sign-in-suppressed";
let autoSignInSuppressed = false;

export function isGoogleFedCmSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "IdentityCredential" in window &&
    typeof window.IdentityCredential === "function" &&
    typeof window.navigator.credentials?.get === "function"
  );
}

export function isGoogleAutoSignInSuppressed(): boolean {
  try {
    return (
      autoSignInSuppressed || window.sessionStorage.getItem(AUTO_SIGN_IN_SUPPRESSED_KEY) === "true"
    );
  } catch {
    return autoSignInSuppressed;
  }
}

export function setGoogleAutoSignInSuppressed(suppressed: boolean): void {
  autoSignInSuppressed = suppressed;
  try {
    if (suppressed) {
      window.sessionStorage.setItem(AUTO_SIGN_IN_SUPPRESSED_KEY, "true");
    } else {
      window.sessionStorage.removeItem(AUTO_SIGN_IN_SUPPRESSED_KEY);
    }
  } catch {
    // Keep logout effective for this page even when storage is blocked.
  }
}

export function disableGoogleAutoSignIn(): void {
  setGoogleAutoSignInSuppressed(true);
  try {
    window.google?.accounts.id.disableAutoSelect();
  } catch {
    // Logout also works when Google's SDK has not loaded or is unavailable.
  }
}

function normalizeLoginHint(email: string | null): string | null {
  const normalized = email?.trim().toLowerCase();
  if (!normalized || !/^[^\s@]+@[^\s@]+$/.test(normalized)) {
    return null;
  }
  return normalized.split("@")[1] === getGoogleDomainHint() ? normalized : null;
}

export function getGoogleLoginHint(): string | null {
  try {
    return normalizeLoginHint(window.localStorage.getItem(LOGIN_HINT_KEY));
  } catch {
    // Storage can be unavailable. Google can still show its account chooser.
    return null;
  }
}

export function clearGoogleLoginHint(): void {
  try {
    window.localStorage.removeItem(LOGIN_HINT_KEY);
  } catch {
    // The sign-in view also bypasses the hint when storage is unavailable.
  }
}

// Call only with an identity accepted by the control plane. This is a UX hint,
// not a credential; Google tokens and server domain checks still authorize access.
export function rememberGoogleLoginEmail(email: string): void {
  try {
    const hint = normalizeLoginHint(email);
    if (hint) {
      window.localStorage.setItem(LOGIN_HINT_KEY, hint);
    } else {
      clearGoogleLoginHint();
    }
  } catch {
    // Remembering the account must never prevent sign-in.
  }
}
